import { Box, Flex, Grid, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import type { SmartContract } from 'types/api/contract';

import config from 'configs/app';
import { CONTRACT_LICENSES } from 'lib/contracts/licenses';
import dayjs from 'lib/date/dayjs';
import { useI18n } from 'lib/i18n/context';
import { getGitHubOwnerAndRepo } from 'ui/contractVerification/utils';
import ContractCertifiedLabel from 'ui/shared/ContractCertifiedLabel';
import LinkExternal from 'ui/shared/links/LinkExternal';

import { ADDRESS_CONTRACT_KEYS } from '../../i18n';
import ContractSecurityAudits from '../audits/ContractSecurityAudits';
import ContractDetailsInfoItem from './ContractDetailsInfoItem';

const rollupFeature = config.features.rollup;

interface Props {
  data: SmartContract;
  isLoading: boolean;
  addressHash: string;
}

const ContractDetailsInfo = ({ data, isLoading, addressHash }: Props) => {
  const { t } = useI18n();
  const cardBg = useColorModeValue('#FFFFFF', '#111111');
  const cardBorder = useColorModeValue('#E9ECEF', '#222222');
  const contractNameWithCertifiedIcon = data ? (
    <Flex alignItems="center">
      { data.name }
      { data.certified && <ContractCertifiedLabel iconSize={ 5 } boxSize={ 5 } ml={ 2 }/> }
    </Flex>
  ) : null;

  const licenseLink = (() => {
    if (!data?.license_type) {
      return null;
    }

    const license = CONTRACT_LICENSES.find((license) => license.type === data.license_type);
    if (!license || license.type === 'none') {
      return null;
    }

    return (
      <LinkExternal href={ license.url }>
        { license.label }
      </LinkExternal>
    );
  })();

  const sourceCodeLink = (() => {
    if (!data.github_repository_metadata?.repository_url || !data.github_repository_metadata?.commit) {
      return null;
    }

    const { owner, repo } = getGitHubOwnerAndRepo(data.github_repository_metadata.repository_url) || {};

    const repoUrl = data.github_repository_metadata.repository_url;
    const commit = data.github_repository_metadata.commit;
    const pathPrefix = data.github_repository_metadata.path_prefix;
    return (
      <LinkExternal href={ `${ repoUrl }/tree/${ commit }${ pathPrefix ? `/${ pathPrefix }` : '' }` }>
        { owner && repo ? `${ owner }/${ repo }` : data.github_repository_metadata.repository_url }
      </LinkExternal>
    );
  })();

  const isStylusContract = data.language === 'stylus_rust';

  return (
    <Box
      mb={ 8 }
      bg={ cardBg }
      border="1px solid"
      borderColor={ cardBorder }
      borderRadius="8px"
      px={{ base: 4, lg: 6 }}
      py={{ base: 4, lg: 5 }}
    >
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} rowGap={ 5 } columnGap={ 8 }>
        { data.name && (
          <ContractDetailsInfoItem
            label={ t(ADDRESS_CONTRACT_KEYS.fields.contractName) }
            content={ contractNameWithCertifiedIcon }
            isLoading={ isLoading }
          />
        ) }
        { data.compiler_version && (
          <ContractDetailsInfoItem
            label={ t(ADDRESS_CONTRACT_KEYS.fields.compilerVersion) }
            content={ data.compiler_version }
            isLoading={ isLoading }
          />
        ) }
        { data.zk_compiler_version && (
          <ContractDetailsInfoItem
            label={ t(ADDRESS_CONTRACT_KEYS.fields.zkCompilerVersion) }
            content={ data.zk_compiler_version }
            isLoading={ isLoading }
          />
        ) }
        { data.evm_version && (
          <ContractDetailsInfoItem
            label={ t(ADDRESS_CONTRACT_KEYS.fields.evmVersion) }
            content={ data.evm_version }
            textTransform="capitalize"
            isLoading={ isLoading }
          />
        ) }
        { licenseLink && (
          <ContractDetailsInfoItem
            label={ t(ADDRESS_CONTRACT_KEYS.fields.license) }
            content={ licenseLink }
            hint={ t(ADDRESS_CONTRACT_KEYS.tooltip.license) }
            isLoading={ isLoading }
          />
        ) }
        { typeof data.optimization_enabled === 'boolean' && !isStylusContract && (
          <ContractDetailsInfoItem
            label={ t(ADDRESS_CONTRACT_KEYS.fields.optimizationEnabled) }
            content={ data.optimization_enabled ? 'true' : 'false' }
            isLoading={ isLoading }
          />
        ) }
        { data.optimization_runs !== null && !isStylusContract && (
          <ContractDetailsInfoItem
            label={ t(
              rollupFeature.isEnabled && rollupFeature.type === 'zkSync' ?
                ADDRESS_CONTRACT_KEYS.fields.optimizationMode :
                ADDRESS_CONTRACT_KEYS.fields.optimizationRuns,
            ) }
            content={ String(data.optimization_runs) }
            isLoading={ isLoading }
          />
        ) }
        { data.package_name && (
          <ContractDetailsInfoItem
            label={ t(ADDRESS_CONTRACT_KEYS.fields.packageName) }
            content={ data.package_name }
            isLoading={ isLoading }
          />
        ) }
        { data.verified_at && (
          <ContractDetailsInfoItem
            label={ t(ADDRESS_CONTRACT_KEYS.fields.verifiedAt) }
            content={ dayjs(data.verified_at).format('llll') }
            wordBreak="break-word"
            isLoading={ isLoading }
          />
        ) }
        { data.file_path && !isStylusContract && (
          <ContractDetailsInfoItem
            label={ t(ADDRESS_CONTRACT_KEYS.fields.contractFilePath) }
            content={ data.file_path }
            wordBreak="break-word"
            isLoading={ isLoading }
          />
        ) }
        { sourceCodeLink && (
          <ContractDetailsInfoItem
            label={ t(ADDRESS_CONTRACT_KEYS.fields.sourceCode) }
            content={ sourceCodeLink }
            isLoading={ isLoading }
          />
        ) }
        { config.UI.hasContractAuditReports && (
          <ContractDetailsInfoItem
            label={ t(ADDRESS_CONTRACT_KEYS.fields.securityAudit) }
            content={ <ContractSecurityAudits addressHash={ addressHash }/> }
            isLoading={ isLoading }
          />
        ) }
      </Grid>
    </Box>
  );
};

export default React.memo(ContractDetailsInfo);
